import { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle, Bell, Users, Search, UserPlus, Loader, Check, RefreshCw } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import axios from 'axios';
import { toast } from 'sonner';

const ChangeStatusModal = ({ job, isOpen, onClose, onStatusChange, isDark = false }) => {
  const [selectedStatus, setSelectedStatus] = useState(job?.status || '');
  const [notes, setNotes] = useState('');
  const [sendNotifications, setSendNotifications] = useState(true);
  const [eligibleUsersCount, setEligibleUsersCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);

  // Update selectedStatus when job changes or modal opens
  useEffect(() => {
    if (isOpen && job?.status) {
      setSelectedStatus(job.status);
      setNotes('');
      setSendNotifications(true);
    }
  }, [isOpen, job?.status]);

  // Re-assign crew states
  const [showReassign, setShowReassign] = useState(false);
  const [crewSearch, setCrewSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [currentHiredCrew, setCurrentHiredCrew] = useState(null);

  // Fetch eligible users count when status changes to SEEKING_APPLICANTS
  useEffect(() => {
    const fetchEligibleCount = async () => {
      if (selectedStatus === 'SEEKING_APPLICANTS' && job) {
        setLoadingCount(true);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/job-notification-preview/${job.id}`
          );
          setEligibleUsersCount(response.data.eligible_count || 0);
        } catch (error) {
          console.error('Failed to fetch eligible users:', error);
          setEligibleUsersCount(0);
        } finally {
          setLoadingCount(false);
        }
      }
    };

    fetchEligibleCount();
  }, [selectedStatus, job]);

  // Fetch current hired crew when modal opens
  useEffect(() => {
    const fetchCurrentCrew = async () => {
      if (isOpen && job && (job.status === 'HIRED' || job.status === 'PENDING_COMPLETION' || job.status === 'OFFER_SENT')) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${job.id}/details`
          );
          if (response.data.hired_crew && response.data.hired_crew.length > 0) {
            setCurrentHiredCrew(response.data.hired_crew[0]);
          }
        } catch (error) {
          console.error('Failed to fetch current crew:', error);
        }
      }
    };
    fetchCurrentCrew();
  }, [isOpen, job]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowReassign(false);
      setCrewSearch('');
      setSearchResults([]);
      setSelectedCrew(null);
      setCurrentHiredCrew(null);
    }
  }, [isOpen]);

  // Search for crew members
  const searchCrewMembers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/search-users`,
        { params: { query, limit: 10 } }
      );
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (crewSearch) {
        searchCrewMembers(crewSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [crewSearch, searchCrewMembers]);

  // Handle re-assign crew
  const handleReassignCrew = async () => {
    if (!selectedCrew) {
      toast.error('Please select a crew member');
      return;
    }

    setReassigning(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${job.id}/reassign-crew`,
        {
          new_crew_user_number: selectedCrew.user_number,
          notes: notes || `Crew reassigned to ${selectedCrew.name}`
        }
      );

      if (response.data.success) {
        toast.success(`Crew reassigned to ${selectedCrew.name}`);
        setShowReassign(false);
        setSelectedCrew(null);
        setCrewSearch('');
        onClose();
        // Trigger refresh
        if (onStatusChange) {
          onStatusChange(job.status, `Crew reassigned to ${selectedCrew.name}`, false);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign crew');
    } finally {
      setReassigning(false);
    }
  };

  if (!isOpen || !job) return null;

  // Check if job can have crew reassigned
  const canReassignCrew = ['HIRED', 'PENDING_COMPLETION', 'OFFER_SENT'].includes(job.status);

  const statuses = [
    { value: 'PENDING_APPROVAL', label: 'Pending Approval', color: 'bg-yellow-500' },
    { value: 'SEEKING_APPLICANTS', label: 'Seeking Applicants', color: 'bg-purple-500' },
    { value: 'OFFER_SENT', label: 'Offer Sent', color: 'bg-orange-500' },
    { value: 'HIRED', label: 'Hired', color: 'bg-blue-500' },
    { value: 'PENDING_COMPLETION', label: 'Pending Completion', color: 'bg-indigo-500' },
    { value: 'PAID_COMPLETED', label: 'Paid/Completed', color: 'bg-green-600' },
    { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-500' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (selectedStatus === 'CANCELLED' && !notes.trim()) {
      alert('Cancellation notes are required');
      return;
    }
    
    onStatusChange(selectedStatus, notes, sendNotifications);
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div className="sz-modal-backdrop" style={{ zIndex: 200 }} onClick={onClose}>
      <div className="sz-modal-card w-full max-w-md overflow-hidden" onClick={stop}>
        {/* Brand gradient header */}
        <div className="sz-modal-header-grad p-[18px_24px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center" style={{ background: "rgba(255,255,255,.18)" }}>
              <RefreshCw className="w-[18px] h-[18px] text-white" />
            </div>
            <h3 className="sz-h3 text-white" style={{ fontSize: 17 }}>Change Job Status</h3>
          </div>
          <button onClick={onClose} className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center text-white" style={{ background: "rgba(255,255,255,.18)" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="szscroll overflow-y-auto p-6 space-y-4">
          <div>
            <Label className={`mb-3 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Job: {job.title}
            </Label>
            <div className="flex items-center gap-2 mb-4">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Current:</span>
              <Badge className={statuses.find(s => s.value === job.status)?.color + ' text-white'}>
                {statuses.find(s => s.value === job.status)?.label || job.status}
              </Badge>
            </div>
          </div>

          <div>
            <Label className={`mb-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              New Status *
            </Label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              required
              className={`w-full h-11 px-3 rounded-lg border ${isDark ? 'bg-[#0f0a1f] border-purple-500/30 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
            >
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {selectedStatus === 'SEEKING_APPLICANTS' && (
            <div className={`p-4 rounded-lg border-2 ${isDark ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="send-notifications"
                  checked={sendNotifications}
                  onCheckedChange={setSendNotifications}
                  className={isDark ? 'border-emerald-500/50 data-[state=checked]:bg-emerald-600' : ''}
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="send-notifications" 
                    className={`text-sm font-semibold cursor-pointer ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}
                  >
                    <Bell className="w-4 h-4 inline mr-1.5" />
                    Send Notification to Eligible Users
                  </Label>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {loadingCount ? (
                      'Calculating eligible users...'
                    ) : (
                      <>
                        <Users className="w-3 h-3 inline mr-1" />
                        <strong>{eligibleUsersCount}</strong> eligible crew member{eligibleUsersCount !== 1 ? 's' : ''} will be notified based on their job type preferences and location radius
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedStatus === 'CANCELLED' && (
            <div className={`p-4 rounded-lg border-2 ${isDark ? 'bg-red-900/20 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className={`text-sm font-bold ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                  Cancellation notes required
                </span>
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter reason for cancellation..."
                required
                rows={4}
                className={`resize-none ${isDark ? 'bg-[#0f0a1f] border-red-500/30 text-white' : 'bg-white border-red-300 text-gray-900'}`}
              />
            </div>
          )}

          {selectedStatus !== 'CANCELLED' && (
            <div>
              <Label className={`mb-2 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Notes (Optional)
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this status change..."
                rows={3}
                className={`resize-none ${isDark ? 'bg-[#0f0a1f] border-purple-500/30 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
              />
            </div>
          )}

          {/* Re-assign Crew Section */}
          {canReassignCrew && (
            <div className={`border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              {!showReassign ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowReassign(true)}
                  className={`w-full ${isDark ? 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Re-assign Crew Member
                </Button>
              ) : (
                <div className={`p-4 rounded-lg border-2 ${isDark ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                      <UserPlus className="w-4 h-4 inline mr-1.5" />
                      Re-assign Crew
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        setShowReassign(false);
                        setSelectedCrew(null);
                        setCrewSearch('');
                        setSearchResults([]);
                      }}
                      className={`text-xs ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Cancel
                    </button>
                  </div>

                  {/* Current Crew */}
                  {currentHiredCrew && (
                    <div className={`mb-3 p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Current Crew:</p>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {currentHiredCrew.name} ({currentHiredCrew.email})
                      </p>
                    </div>
                  )}

                  {/* Search Input */}
                  <div className="relative mb-3">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <Input
                      type="text"
                      value={crewSearch}
                      onChange={(e) => setCrewSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className={`pl-9 ${isDark ? 'bg-[#0f0a1f] border-blue-500/30 text-white' : 'bg-white border-blue-300'}`}
                    />
                    {searchLoading && (
                      <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />
                    )}
                  </div>

                  {/* Selected Crew */}
                  {selectedCrew && (
                    <div className={`mb-3 p-2 rounded border-2 ${isDark ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-400'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>New Crew:</p>
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {selectedCrew.name}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {selectedCrew.email}
                          </p>
                        </div>
                        <Check className="w-5 h-5 text-green-500" />
                      </div>
                    </div>
                  )}

                  {/* Search Results */}
                  {searchResults.length > 0 && !selectedCrew && (
                    <div className={`max-h-40 overflow-y-auto rounded border ${isDark ? 'bg-[#0f0a1f] border-gray-700' : 'bg-white border-gray-200'}`}>
                      {searchResults.map((user) => (
                        <button
                          key={user.user_number}
                          type="button"
                          onClick={() => {
                            setSelectedCrew(user);
                            setCrewSearch('');
                            setSearchResults([]);
                          }}
                          className={`w-full text-left px-3 py-2 border-b last:border-b-0 ${
                            isDark
                              ? 'border-gray-700 hover:bg-blue-900/30'
                              : 'border-gray-100 hover:bg-blue-50'
                          }`}
                        >
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {user.name}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.email} • #{user.user_number}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}

                  {crewSearch.length >= 2 && searchResults.length === 0 && !searchLoading && !selectedCrew && (
                    <p className={`text-xs text-center py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      No users found
                    </p>
                  )}

                  {/* Re-assign Button */}
                  {selectedCrew && (
                    <Button
                      type="button"
                      onClick={handleReassignCrew}
                      disabled={reassigning}
                      className="w-full mt-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                    >
                      {reassigning ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Reassigning...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Confirm Re-assignment
                        </>
                      )}
                    </Button>
                  )}

                  <p className={`text-[10px] mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    This will update the job assignment, invoices, and notify both crew members.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="sz-btn sz-btn-outline flex-1">Cancel</button>
            <button type="submit" className="sz-btn sz-btn-primary flex-1">Change Status</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeStatusModal;
